import { useEffect, useState } from 'react';
import { useLanguage } from '../i18n';
import { getCacheTimestamp, refreshVideosFromApi } from '../youtube';

// Only show on localhost / 127.0.0.1 / private LAN ranges. The button burns
// YouTube API quota, so we don't expose it on the live site where bots and
// random visitors could trigger it. (Defence-in-depth: the API key should
// also have HTTP-referrer restrictions in Google Cloud — see
// https://console.cloud.google.com/apis/credentials.)
function isLocalDev(): boolean {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return (
    h === 'localhost' ||
    h === '127.0.0.1' ||
    h === '::1' ||
    h === '[::1]' ||
    // 10.0.0.0/8, 172.16.0.0/12, 192.168.0.0/16 — typical LAN ranges
    /^10\./.test(h) ||
    /^192\.168\./.test(h) ||
    /^172\.(1[6-9]|2\d|3[01])\./.test(h)
  );
}

/**
 * Manual "Refresh" button — visible only on localhost / LAN. Clicking it
 * confirms with the user, then hits the YouTube API and overwrites the
 * localStorage cache. The page reloads on success so every component picks
 * up the new list.
 *
 * The tooltip shows the timestamp of the last successful refresh, so the
 * user knows when they last paid quota.
 */
export default function RefreshButton() {
  const { t } = useLanguage();
  const [busy, setBusy] = useState(false);
  const [tsLabel, setTsLabel] = useState<string>('');

  // Defence-in-depth: don't even render the button when off-localhost.
  if (!isLocalDev()) return null;

  useEffect(() => {
    const ts = getCacheTimestamp();
    setTsLabel(
      ts && ts > 0
        ? `${t('refresh', 'updated')}: ${new Date(ts).toLocaleString()}`
        : t('refresh', 'neverUpdated'),
    );
  }, [t, busy]);

  const onClick = async () => {
    if (busy) return;
    if (!window.confirm(t('refresh', 'confirm'))) return;
    setBusy(true);
    try {
      const fresh = await refreshVideosFromApi();
      // Reload so every page re-reads the new cache. Simpler than threading
      // refresh state through pages + contexts for a one-shot manual action.
      window.alert(t('refresh', 'success').replace('{n}', String(fresh.length)));
      window.location.reload();
    } catch (e) {
      window.alert(`${t('error')}: ${(e as Error).message}`);
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      className="refresh-btn"
      onClick={onClick}
      disabled={busy}
      title={`${t('refresh', 'tooltip')}\n${tsLabel}`}
      aria-label={t('refresh', 'button')}
    >
      <span className="refresh-icon" aria-hidden="true">⟳</span>
      <span className="refresh-label">
        {busy ? t('refresh', 'working') : t('refresh', 'button')}
      </span>
    </button>
  );
}
